/**
 * Minimum billing volume (review #695, as Shayna rewrote it 2026-10-18; MEASURE-RULES
 * Rule 10.9).
 *
 * > If a biller's monthly billed claim volume is more than 10% below the average billed
 * > claim volume of the bottom 25% of employees within the same job title, their final
 * > monthly score will be overridden to 0.
 *
 * Per job title and month:
 *   1. the people eligible for a full-month score that month (`fullMonth`), with claims;
 *   2. the bottom 25% of them by billed claims (ceil, so at least one);
 *   3. their average billed claims;
 *   4. the minimum = 90% of that average.
 * A biller below the minimum in one of THEIR full months is Not rated for the month ("Below
 * Minimum Billing Volume"; a real 0 until review #985, 2026-11-04); the computed score stays
 * visible on hover. A month that is not full for
 * them (hire, leaving, open, short hours) is not tested (Yehuda, 2026-10-19). No benchmark
 * (fewer than MIN_BENCHMARK eligible people in the title that month) is no test.
 *
 * Billed claims are the ones the score counts: weighted where the Weighted claims switch
 * is on. The benchmark is over active staff only, as the peer averages are.
 */
import type { TouchMonthRow } from '@/data/types'

export const MINIMUM_VOLUME_LABEL = 'Below Minimum Billing Volume'

/** The bottom share of the job title the benchmark averages. */
export const BOTTOM_SHARE = 0.25
/** The minimum is this share of that average: "more than 10% below". */
export const MINIMUM_SHARE = 0.9
/**
 * Fewer eligible people than this in a job title that month: no benchmark, no test.
 * The ranking cohort's floor (MEASURE-RULES §10.7), so a title too small to rank is
 * not benchmarked either.
 */
export const MIN_BENCHMARK = 5
/** A month paid under this share of the person's usual hours is not a full month. */
export const FULL_HOURS_SHARE = 0.8

/** One job title's benchmark for one month. */
export interface VolumeBar {
  /** Average billed claims of the bottom quarter. */
  avg: number
  /** The minimum: MINIMUM_SHARE of `avg`. */
  bar: number
  /** Eligible people the quarter was taken of. */
  n: number
}

/** The claims the score counts in a month row. */
export const billedOf = (r: Pick<TouchMonthRow, 'billedClaims' | 'weightedClaims'>) =>
  r.weightedClaims ?? r.billedClaims

/** Paid hours less PTO: `laborHours` counts every paid hour, PTO and OT included (§8.3). */
const workedHours = (r: Pick<TouchMonthRow, 'laborHours' | 'ptoHours'>): number | null =>
  r.laborHours == null ? null : r.laborHours - (r.ptoHours ?? 0)

/**
 * A person's usual monthly hours: the median of their worked hours over the closed
 * months given, months with none left out. Null when no month has hours (a bundle
 * built before payroll hours, 2026-09-30): the hours test is then skipped.
 */
export function usualHours(rows: readonly Pick<TouchMonthRow, 'laborHours' | 'ptoHours'>[]): number | null {
  const h = rows
    .map(workedHours)
    .filter((v): v is number => v !== null && v > 0)
    .sort((a, b) => a - b)
  if (h.length === 0) return null
  const mid = Math.floor(h.length / 2)
  return h.length % 2 ? h[mid]! : (h[mid - 1]! + h[mid]!) / 2
}

/** What `fullMonth` reads about a person. */
export interface Eligibility {
  /** 'YYYY-MM-DD' or null. */
  hireDate: string | null
  terminationDate: string | null
  /** `usualHours` over their closed months. */
  usual: number | null
}

/**
 * Eligible for a full-month score (and so for the benchmark) in `row`'s month:
 *   - not the month they were hired, nor the month they left or after;
 *   - not a month still open (running, or payroll pending);
 *   - worked hours (paid less PTO) at least FULL_HOURS_SHARE of their usual hours,
 *     which leaves out leave, long PTO and part months. Skipped where the bundle has
 *     no hours.
 */
export function fullMonth(row: Pick<TouchMonthRow, 'month' | 'laborHours' | 'ptoHours'>, e: Eligibility, open: ReadonlySet<string>): boolean {
  if (open.has(row.month)) return false
  if (e.hireDate && e.hireDate.slice(0, 7) === row.month) return false
  if (e.terminationDate && e.terminationDate.slice(0, 7) <= row.month) return false
  if (e.usual !== null) {
    const w = workedHours(row)
    if (w === null || w < FULL_HOURS_SHARE * e.usual) return false
  }
  return true
}

/** The benchmark for one title-month, from its eligible billers' billed claims. */
export function benchmark(billed: readonly number[]): VolumeBar | null {
  const xs = billed.filter((v) => v > 0).sort((a, b) => a - b)
  if (xs.length < MIN_BENCHMARK) return null
  const k = Math.max(1, Math.ceil(xs.length * BOTTOM_SHARE))
  const avg = xs.slice(0, k).reduce((a, b) => a + b, 0) / k
  return { avg, bar: MINIMUM_SHARE * avg, n: xs.length }
}

/** A person as `volumeBars` reads them. */
export interface VolumePerson extends Eligibility {
  jobTitle: string
  terminated: boolean
  months: readonly TouchMonthRow[]
}

/**
 * Every job title's benchmark for every month the people have, keyed `title|month`.
 * Over active staff, the full months only.
 */
export function volumeBars(people: readonly VolumePerson[], open: ReadonlySet<string>): Map<string, VolumeBar> {
  const billed = new Map<string, number[]>()
  for (const p of people) {
    if (p.terminated) continue
    for (const r of p.months) {
      if (!fullMonth(r, p, open)) continue
      const k = `${p.jobTitle}|${r.month}`
      const list = billed.get(k)
      if (list) list.push(billedOf(r))
      else billed.set(k, [billedOf(r)])
    }
  }
  const out = new Map<string, VolumeBar>()
  for (const [k, xs] of billed) {
    const b = benchmark(xs)
    if (b) out.set(k, b)
  }
  return out
}

/**
 * The test over several months, as the range and trailing scores need it: the
 * person's billed claims over the months given (`derive` passes their FULL months only)
 * that have a benchmark, against the sum of those months' minimums. Null when no month
 * has one. On one month it is the monthly test exactly.
 */
export function rangeVolume(
  months: readonly TouchMonthRow[],
  barOf: (month: string) => VolumeBar | undefined,
): { claims: number; bar: number; avg: number } | null {
  let claims = 0
  let bar = 0
  let avg = 0
  let any = false
  for (const r of months) {
    const b = barOf(r.month)
    if (!b) continue
    any = true
    claims += billedOf(r)
    bar += b.bar
    avg += b.avg
  }
  return any ? { claims, bar, avg } : null
}
